import {
  convertEventSelectToTarget,
  formatDataToLabelValue,
  FormFeedBack,
  MainButton,
  PageWrapper,
  Select,
  STATUSPAGE,
  locale,
} from '@repo/ui';
import { GetAllCoursesUserUseCaseInterface } from 'domain/services/courses/get-all-courses-user';
import {
  GetAllGroupsUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import {
  getPtBrRecurrenceType,
  RECURRENCE_TYPE,
} from 'domain/value-objects/recurrence-type';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import {
  Card,
  CardBody,
  CardFooter,
  Col,
  Form,
  Input,
  Label,
  Row,
} from 'reactstrap';
import './styles.scss';

import { AlertPushNotification } from '../components/alert-push-notification';
import { CustomSchedulingPushNotification } from '../components/custom-scheduling-push-notification';
import { TextAreaMessage } from '../components/TextAreaMessage';
import { usePushNotification } from '../hooks/usePushNotification';

interface IProps {
  getAllGroupsUser?: GetAllGroupsUserUseCaseInterface;
  getAllCoursesUser?: GetAllCoursesUserUseCaseInterface;
  getAllOrganizationsUser?: GetAllOrganizationsUserUseCaseInterface;
}

const profileOptions = [
  { value: PROFILE_TYPE.TEACHER, label: 'Professores' },
  { value: PROFILE_TYPE.PRECEPTOR, label: 'Preceptores' },
  { value: PROFILE_TYPE.STUDENT, label: 'Estudantes' },
];

const recurrenceOptions = [
  { value: RECURRENCE_TYPE.DONT_REPEAT, label: 'Não se repete' },
  { value: RECURRENCE_TYPE.DAILY, label: 'Diariamente' },
  { value: RECURRENCE_TYPE.WEEKLY, label: 'Semanalmente' },
  { value: RECURRENCE_TYPE.MONTHLY, label: 'Mensalmente' },
  { value: RECURRENCE_TYPE.YEARLY, label: 'Anualmente' },
  {
    value: RECURRENCE_TYPE.DAYS_OF_WEEK,
    label: 'Dias da semana (Segunda à sexta-feira)',
  },
  { value: RECURRENCE_TYPE.CUSTOM, label: 'Personalizado' },
];

const customRecurrenceUnitOptions = [
  { value: RECURRENCE_TYPE.DAILY, label: 'Dia(s)' },
  { value: RECURRENCE_TYPE.WEEKLY, label: 'Semana(s)' },
  { value: RECURRENCE_TYPE.MONTHLY, label: 'Mês(es)' },
  { value: RECURRENCE_TYPE.YEARLY, label: 'Ano(s)' },
];

export const PushNotification = ({
  getAllGroupsUser,
  getAllCoursesUser,
  getAllOrganizationsUser,
}: IProps) => {
  //hooks
  const { organizationsUser, loadingOrganizationsUser } = useAppSelector(
    (state) => state.Organizations,
  );

  const { coursesUser, loadingCoursesUser } = useAppSelector(
    (state) => state.Courses,
  );

  const { groupsUser } = useAppSelector((state) => state.Groups);

  const {
    requestGetAllGroupsUser,
    requestGetAllCoursesUser,
    requestGetAllOrganizationsUser,
    loading,
    validation,
    statusPushNotification,
    showAlert,
    setShowAlert,
  } = usePushNotification({
    getAllGroupsUser,
    getAllCoursesUser,
    getAllOrganizationsUser,
  });

  //Effects
  useEffect(() => {
    requestGetAllOrganizationsUser({
      course: validation.values?.course?.value,
    });
  }, [validation.values?.course?.value]);

  useEffect(() => {
    const recurrenceValue = validation.values.recurrencePushNotification?.value;

    validation.setFieldValue('customRecurrenceInterval', 1);
    validation.setFieldValue('customRecurrenceUnit', {
      value: RECURRENCE_TYPE.DAILY,
      label: getPtBrRecurrenceType(RECURRENCE_TYPE.DAILY),
    });
    validation.setFieldValue('endAtPushNotification', 'date');
    validation.setFieldValue('endAtDate', null);
    validation.setFieldValue('endAtOccurrences', 1);

    if (recurrenceValue === RECURRENCE_TYPE.DAYS_OF_WEEK) {
      validation.setFieldValue('weeklyDays', [1, 2, 3, 4, 5]);
    } else if (recurrenceValue === RECURRENCE_TYPE.WEEKLY) {
      const selectedDate = validation.values.scheduledTo;
      if (selectedDate) {
        validation.setFieldValue('weeklyDays', [selectedDate.getDay()]);
      } else {
        validation.setFieldValue('weeklyDays', []);
      }
    } else {
      validation.setFieldValue('weeklyDays', []);
    }
  }, [
    validation.values.recurrencePushNotification?.value,
    validation.values.scheduledTo,
  ]);

  return (
    <PageWrapper title="Enviar Notificação">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold">Enviar Push Notification</h4>
        </div>
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Card className="p-3">
          <Row>
            <Col md={3}>
              <Label htmlFor="course">Organização</Label>
              <Select
                isClearable
                id="entity"
                name="entity"
                placeholder="Todas as Organizações"
                value={validation.values.entity}
                isLoading={loadingOrganizationsUser}
                onChange={(e) => {
                  if (!e) {
                    validation.resetForm();
                  }
                  requestGetAllCoursesUser(e?.value || '');
                  validation.setValues({
                    ...validation.values,
                    group: null,
                  });
                  validation.handleChange(
                    convertEventSelectToTarget('entity', e),
                  );
                }}
                onBlur={validation.handleBlur}
                options={formatDataToLabelValue(
                  organizationsUser as never,
                  'id',
                  'nome',
                )}
              />
            </Col>
            <Col md={3}>
              <Label htmlFor="course">Curso</Label>
              <Select
                isClearable
                id="course"
                name="course"
                placeholder="Todos os cursos"
                onBlur={validation.handleBlur}
                value={validation?.values?.course}
                options={formatDataToLabelValue(
                  coursesUser as never,
                  'id',
                  'nome',
                )}
                onChange={(e) => {
                  requestGetAllGroupsUser({
                    entityId: validation.values.entity?.value,
                    courseId: e?.value,
                  });
                  validation.setValues({
                    ...validation.values,
                    group: null,
                  });
                  validation.handleChange(
                    convertEventSelectToTarget('course', e),
                  );
                }}
                isDisabled={loadingCoursesUser === true}
              />
            </Col>
            <Col md={3}>
              <Label htmlFor="course">Grupo</Label>

              <Select
                isClearable
                id="group"
                isMulti
                name="group"
                placeholder="Todos os grupos"
                onBlur={validation.handleBlur}
                value={validation?.values?.group}
                options={formatDataToLabelValue(
                  groupsUser as never,
                  'id',
                  'nome',
                )}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('group', e),
                  );
                }}
                isDisabled={
                  !(validation.values.entity && validation.values.course)
                }
              />
            </Col>
            <Col md={3}>
              <Label htmlFor="course">Perfis</Label>

              <Select
                isClearable
                id="profile"
                name="profile"
                placeholder="Todos os perfis"
                isMulti
                onBlur={validation.handleBlur}
                value={validation?.values?.profile}
                options={profileOptions}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('profile', e),
                  );
                }}
                invalid={
                  !!(validation?.touched.profile && validation?.errors?.profile)
                }
              />
              <FormFeedBack
                error={validation.errors?.profile as any}
                isInvalid={
                  !!(validation.touched.profile && validation.errors?.profile)
                }
              />
            </Col>
          </Row>
        </Card>

        <CardBody>
          <Card className="">
            <h5 className="fw-bold p-3">Push Notification</h5>
            <CardBody className="mt-0 pt-0">
              {showAlert && (
                <AlertPushNotification
                  setShowAlert={setShowAlert}
                  statusPushNotification={statusPushNotification}
                />
              )}

              <Row>
                <Col md={12}>
                  <Label htmlFor="course">Título</Label>
                  <Input
                    id="title"
                    maxLength={50}
                    type="text"
                    name="title"
                    className="form-control"
                    value={validation.values.title}
                    onBlur={validation.handleBlur}
                    onChange={validation.handleChange}
                    invalid={
                      !!(validation?.touched.title && validation?.errors?.title)
                    }
                  />

                  <FormFeedBack
                    error={validation.errors?.title as any}
                    isInvalid={
                      !!(validation.touched.title && validation.errors?.title)
                    }
                  />
                </Col>
                <TextAreaMessage
                  error={
                    !!(validation.touched.message && validation.errors?.message)
                  }
                  errorMessage={validation.errors?.message as string}
                  value={validation.values?.message}
                  handleChange={validation.handleChange}
                />
              </Row>

              {/*  <Row>
                <Col md={3}>
                  <div className="form-check my-3">
                    <Input
                      id="schedulePushNotification"
                      type="checkbox"
                      name="schedulePushNotification"
                      className="form-check-input"
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      checked={validation?.values?.schedulePushNotification}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="schedulePushNotification"
                    >
                      Agendar notificação
                    </label>
                  </div>
                </Col>
              </Row> */}

              {validation.values.schedulePushNotification && (
                <Row>
                  <Col md={3}>
                    <Label htmlFor="course">Recorrência</Label>
                    <Select
                      id="recurrencePushNotification"
                      name="recurrencePushNotification"
                      value={validation.values.recurrencePushNotification}
                      onChange={(e) => {
                        validation.handleChange(
                          convertEventSelectToTarget(
                            'recurrencePushNotification',
                            e,
                          ),
                        );
                      }}
                      onBlur={validation.handleBlur}
                      options={recurrenceOptions}
                      invalid={
                        !!(
                          validation?.touched.recurrencePushNotification &&
                          validation?.errors?.recurrencePushNotification
                        )
                      }
                    />
                    <FormFeedBack
                      error={
                        validation.errors?.recurrencePushNotification as string
                      }
                      isInvalid={
                        !!(
                          validation.touched.recurrencePushNotification &&
                          validation.errors?.recurrencePushNotification
                        )
                      }
                    />
                  </Col>

                  <Col md={3}>
                    <Label
                      htmlFor="periodPushNotification"
                      className="form-label"
                    >
                      Agendar para
                    </Label>
                    <Flatpickr
                      className="form-control"
                      id="scheduleToPushNotification"
                      name="scheduleToPushNotification"
                      placeholder="Selecione o dia e horário"
                      value={validation.values.scheduledTo}
                      onChange={(selectedDates) => {
                        let selectedDate = selectedDates[0] || null;

                        if (selectedDate) {
                          const minutes = selectedDate.getMinutes();
                          const roundedMinutes = Math.round(minutes / 30) * 30;
                          selectedDate.setMinutes(roundedMinutes % 60);
                        }

                        validation.setFieldValue('scheduledTo', selectedDate);
                      }}
                      onBlur={validation.handleBlur}
                      options={{
                        enableTime: true,
                        dateFormat: 'd/m/Y H:i',
                        time_24hr: true,
                        minDate: 'today',
                        minuteIncrement: 30,
                        locale: locale,
                        defaultHour: 8,
                        defaultMinute: 0,
                      }}
                    />
                  </Col>
                </Row>
              )}

              {validation.values.schedulePushNotification &&
                validation.values.recurrencePushNotification?.value !==
                  undefined &&
                validation.values.recurrencePushNotification?.value !== null &&
                validation.values.recurrencePushNotification?.value !==
                  RECURRENCE_TYPE.DONT_REPEAT && (
                  <CustomSchedulingPushNotification
                    validation={validation}
                    customRecurrenceUnitOptions={customRecurrenceUnitOptions}
                  />
                )}
            </CardBody>

            <CardFooter className="d-flex justify-content-end">
              <MainButton
                type="submit"
                isDisabled={loading === STATUSPAGE.LOADING}
                isLoading={loading === STATUSPAGE.LOADING}
              >
                Enviar
              </MainButton>
            </CardFooter>
          </Card>
        </CardBody>
      </Form>
    </PageWrapper>
  );
};
